import { useState, useCallback } from 'react'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  GreenContainer,
  GreenContainerQuestionWrapper,
  ClaimsContainer
} from 'src/components/application/defaultComponents'
import ApplicationPrebuiltFooter from 'src/components/application/PrebuiltBodyFooter'
import { useApplication } from 'src/context/application'
import { LeftBar } from 'src/components/basic/LeftBar'
import { FlexDiv } from 'src/components/application/formComponents/styles'
import StepBullet from 'src/components/basic/StepBullet'
import OptionButton from 'src/components/basic/OptionButton'
import ClaimItemForm from 'src/components/application/formComponents/ClaimItemForm'
import flagWithCheckImg from 'src/assets/applicationFormsImgs/flag_with_check.svg'
import ApplicationData, { ClaimData } from 'src/dtos/applicationData'
import { ClaimDataWithId } from 'src/dtos/custom'
import Button from 'src/components/basic/Button'
import { generateRandomNumberId } from 'src/utils/math'
import { object, string } from 'yup'

const Claims = () => {
  const { application } = useApplication()
  const newAppJudgeErrorCodes: string[] = ['EC148', 'PH', 'PH', 'EC149', 'EC150', 'EC151', 'EC156', 'EC157', 'EC158', 'EC159', 'EC160',
                                            'EC161', 'EC162', 'EC163', 'EC164', 'EC165', 'EC166', 'EC167', 'EC168', 'EC169', 'EC170', 'EC152',
                                            'EC153', 'EC154', 'EC155']
  
  const [claims, setClaims] = useState<ClaimDataWithId[]>(() => {
    return application?.judgeApplicationData?.insuranceSection?.claims?.map(claim => ({ ...claim, id: generateRandomNumberId() })) || 
      [{ id: generateRandomNumberId() }]
  })
  const [isIncorrect, setIsIncorrect] = useState(false)
  const [hasClaimsAsserted, setHasClaimsAsserted] = useState(() => {
    if(application?.judgeApplicationData?.insuranceSection?.pastLiability) {
      const { hasClaimsAsserted } = application?.judgeApplicationData?.insuranceSection?.pastLiability

      return {
        isVisible: !!hasClaimsAsserted,
        value: hasClaimsAsserted
      }
    }
    return { isVisible: false, value: false }
  })
  const [hasActsToBaseClaim, setHasActsToBaseClaim] = useState(() => {
    if(application?.judgeApplicationData?.insuranceSection?.pastLiability) {
      const { hasActsToBaseClaim } = application?.judgeApplicationData?.insuranceSection?.pastLiability

      return {
        isVisible: !!hasActsToBaseClaim,
        value: hasActsToBaseClaim
      }
    }
    return { isVisible: false, value: false }
  })

  const isAllSet = () => {
    return !hasClaimsAsserted.value && !hasActsToBaseClaim.value
  }

  const updateClaims = useCallback((index: number, data: Partial<ClaimData>) => {
    setClaims(prev => prev.map((pv, idx) => idx === index ? { ...pv, ...data } : pv))
  }, [])

  const handleAddAnotherClaim = () => {
    setClaims(prev => [...prev, { id: generateRandomNumberId() }])
  }

  const updatedApplicationData = (): ApplicationData => ({ 
    ...application,
    currentPage: window.location.pathname,
    judgeApplicationData: {
      ...application?.judgeApplicationData,
      insuranceSection: {
        ...application?.judgeApplicationData?.insuranceSection,
        pastLiability: {
          ...application?.judgeApplicationData?.insuranceSection?.pastLiability,
          hasClaimsAsserted: hasClaimsAsserted.value,
          hasActsToBaseClaim: hasActsToBaseClaim.value,
        },
        claims: claims.map(claim => {
          const { id, ...rest } = claim
          return rest
        })
      }
    }
  })

  const validatePage = () => {
    const validation = claims.map(claim => {
      try {
        if(!!claim?.claimDate) {
          string().trim().length(10).validDate('Invalid Date').validateSync(claim?.claimDate)
        }
        if(!!claim?.allegedErrorDate) {
          string().trim().length(10).validDate('Invalid Date').validateSync(claim?.allegedErrorDate)
        }
        return true
      } catch(e) {
        return false
      }
    })

    return !validation.some(valid => valid === false)
  }

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width='80%'>
        <DefaultTitle>Please fill out the following regarding your claim.</DefaultTitle>
        <GreenContainer>
          <div className='green-container-content'>
            <h6>You’re being asked to fill out this supplemental claim information based on your answers to the following questions.</h6>
            <span>If this information is incorrect, please change your answers.</span>
            <div>
              <LeftBar />
              <GreenContainerQuestionWrapper>
                {hasClaimsAsserted.isVisible ? (
                  <div className='green-container-question-div'>
                    <StepBullet>2</StepBullet>
                    <FlexDiv dir='column' gap="24px">
                      <p>
                        Has the applicant ever had any professional liability claims asserted or action filed against him or her, or has a claim been paid on behalf of the applicant?
                      </p>
                      <FlexDiv gap="16px">
                        <OptionButton
                          checked={hasClaimsAsserted.value === true}
                          onChange={() => setHasClaimsAsserted(prev => ({ ...prev, value: true }))}
                        >
                          Yes
                        </OptionButton>
                        <OptionButton
                          checked={hasClaimsAsserted.value === false}
                          onChange={() => setHasClaimsAsserted(prev => ({ ...prev, value: false }))}
                        >
                          No
                        </OptionButton>
                      </FlexDiv>
                    </FlexDiv>
                  </div>
                ) : null}
                {hasActsToBaseClaim.isVisible ? (
                  <div className='green-container-question-div'>
                    <StepBullet>5</StepBullet>
                    <FlexDiv dir='column' gap="24px">
                      <p>
                        Does the applicant know of any circumstance, act, error or omission that might form the basis of a claim against the applicant?
                      </p>
                      <FlexDiv gap="16px">
                        <OptionButton
                          checked={hasActsToBaseClaim.value === true}
                          onChange={() => setHasActsToBaseClaim(prev => ({ ...prev, value: true }))}
                        >
                          Yes
                        </OptionButton>
                        <OptionButton
                          checked={hasActsToBaseClaim.value === false}
                          onChange={() => setHasActsToBaseClaim(prev => ({ ...prev, value: false }))}
                        >
                          No
                        </OptionButton>
                      </FlexDiv>
                    </FlexDiv>
                  </div>
                ) : null}
              </GreenContainerQuestionWrapper>
            </div>
          </div>
        </GreenContainer>
        {isAllSet() ? (
          <GreenContainer>
            <div className='claim-all-set'>
              <div className='claim-all-set-icon'>
                <img src={flagWithCheckImg} alt="All Set" />
              </div>
              <div className='claim-all-set-message'>
                <h3>All Set!</h3>
                <h4>You no longer need to complete this section.</h4>
              </div>
            </div>
          </GreenContainer>
        ) : (
          <ClaimsContainer>
            <span className='claims-form-header-subtitle'>PLEASE FILL OUT THE FOLLOWING TO THE BEST OF YOUR ABILITY</span>
            <span className='claims-form-header-subtitle'>If you do not know, leave blank.</span>
            {claims.map((claim, index) => (
              <ClaimItemForm
                key={claim.id}
                claim={claim}
                claimIndex={index}
                attorneys={application?.attorneysSection?.attorneys}
                setClaim={(data) => updateClaims(index, data)}
                isJudge
                isIncorrect={isIncorrect && !validatePage()}
                errorCodes={newAppJudgeErrorCodes.map(e => `${e}:${index}`)}
              />
            ))}
            <div className='claims-add-button'>
              <Button variant='secondary' onClick={handleAddAnotherClaim}>
                Add another claim
              </Button>
            </div>
          </ClaimsContainer>
        )}
      </ApplicationBodyContent>
      <ApplicationPrebuiltFooter
        goBackRoute="/judge/insurance/past-liability"
        nextRoute="/judge/quote/details"
        updatedApplication={updatedApplicationData()}
        isFormValid={validatePage()}
        beforeSaveContinueClick={() => !validatePage() ? setIsIncorrect(true) : null}
      />
    </ApplicationBodyContainer>
  )
}

export default Claims